import { useEffect, useState, useCallback, useMemo } from "react";
import { swapRequestsApi, opsConsoleApi } from "@/api";
import {
  ArrowRightLeft, Plus, Check, X, Clock, ChevronDown,
  RefreshCw, Search, Filter, AlertCircle, CheckCircle2,
  XCircle, Timer, User, Plane,
} from "lucide-react";
import { toast } from "sonner";
import { exportCSV, todayStr } from "@/utils/exportUtils";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_META = {
  pending_response: { label: "Awaiting Response", color: "text-amber-400",  border: "border-amber-500/30", bg: "bg-amber-500/10",  icon: Timer         },
  pending_approval: { label: "Awaiting Approval", color: "text-blue-400",   border: "border-blue-500/30",  bg: "bg-blue-500/10",   icon: Clock         },
  approved:         { label: "Approved",          color: "text-green-400",  border: "border-green-500/30", bg: "bg-green-500/10",  icon: CheckCircle2  },
  rejected:         { label: "Rejected by Ops",   color: "text-red-400",    border: "border-red-500/30",   bg: "bg-red-500/10",    icon: XCircle       },
  rejected_by_peer: { label: "Declined by Crew",  color: "text-orange-400", border: "border-orange-500/30",bg: "bg-orange-500/10", icon: XCircle       },
  cancelled:        { label: "Cancelled",         color: "text-muted-foreground", border: "border-border", bg: "bg-secondary/20",  icon: X             },
};

const TABS = [
  { key: "ALL",              label: "All",              count_key: "total"            },
  { key: "pending_response", label: "Awaiting Response", count_key: "pending_response" },
  { key: "pending_approval", label: "Awaiting Approval", count_key: "pending_approval" },
  { key: "approved",         label: "Approved",          count_key: "approved"         },
  { key: "rejected",         label: "Rejected",          count_key: "rejected"         },
];

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60)  return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// ─── New Swap Request Modal ───────────────────────────────────────────────────

function NewSwapModal({ onClose, onCreated }) {
  const [allCrew,    setAllCrew]    = useState([]);
  const [fromQuery,  setFromQuery]  = useState("");
  const [toQuery,    setToQuery]    = useState("");
  const [fromCrew,   setFromCrew]   = useState(null);
  const [toCrew,     setToCrew]     = useState(null);
  const [flight,     setFlight]     = useState("");
  const [reason,     setReason]     = useState("");
  const [loading,    setLoading]    = useState(false);
  const [loadingCrew,setLoadingCrew]= useState(true);

  useEffect(() => {
    opsConsoleApi.getStatus()
      .then(r => setAllCrew(Object.values(r.data.board || {}).flat()))
      .catch(() => {})
      .finally(() => setLoadingCrew(false));
  }, []);

  function filterCrew(query, exclude) {
    const q = query.toLowerCase();
    return allCrew.filter(c =>
      c.id !== exclude?.id &&
      (!fromCrew || _sameCat(c.rank, fromCrew.rank) || !fromCrew) &&
      (c.name.toLowerCase().includes(q) || c.employee_id?.toLowerCase().includes(q) || c.rank?.toLowerCase().includes(q) || c.base?.toLowerCase().includes(q))
    ).slice(0, 6);
  }

  function _sameCat(a, b) {
    const cat = r => {
      const lc = r.toLowerCase();
      if (lc.includes("captain")) return "captain";
      if (lc.includes("first officer") || lc.startsWith("fo")) return "fo";
      return "cabin_crew";
    };
    return cat(a) === cat(b);
  }

  async function handleSubmit() {
    if (!fromCrew || !toCrew || !flight.trim()) return;
    setLoading(true);
    try {
      await swapRequestsApi.submit({
        from_crew_id: fromCrew.id,
        to_crew_id:   toCrew.id,
        flight_number: flight.trim().toUpperCase(),
        reason,
      });
      toast.success("Swap request submitted — awaiting response from " + toCrew.name);
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit swap request");
    } finally { setLoading(false); }
  }

  const fromResults = fromQuery.length > 0 ? filterCrew(fromQuery, null) : [];
  const toResults   = toQuery.length > 0   ? filterCrew(toQuery, fromCrew) : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="new-swap-modal">
      <div className="bg-card border border-border rounded-sm w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">New Swap Request</div>
            <div className="text-sm font-black text-foreground mt-0.5">Crew Self-Service</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-4">
          {loadingCrew ? (
            <div className="text-xs text-muted-foreground text-center py-4">Loading crew...</div>
          ) : (
            <>
              {/* From crew */}
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5 block">
                  Requesting Crew Member
                </label>
                {fromCrew ? (
                  <div className="flex items-center justify-between p-2.5 bg-background/60 border border-primary/30 rounded-sm">
                    <div>
                      <div className="text-xs font-semibold text-foreground">{fromCrew.name}</div>
                      <div className="text-[11px] text-muted-foreground">{fromCrew.rank} · {fromCrew.base}</div>
                    </div>
                    <button onClick={() => { setFromCrew(null); setToCrew(null); setFromQuery(""); }} className="text-muted-foreground hover:text-red-400"><X size={12} /></button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search size={11} className="absolute left-2.5 top-2.5 text-muted-foreground" />
                    <input value={fromQuery} onChange={e => setFromQuery(e.target.value)} placeholder="Search by name, ID or base..."
                      className="w-full pl-7 pr-3 py-2 bg-background border border-border rounded-sm text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                      data-testid="from-crew-search" />
                    {fromResults.length > 0 && (
                      <div className="absolute z-20 top-full mt-1 left-0 right-0 bg-card border border-border rounded-sm shadow-xl max-h-44 overflow-y-auto">
                        {fromResults.map(c => (
                          <button key={c.id} onClick={() => { setFromCrew(c); setFromQuery(""); setToCrew(null); }}
                            className="w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-secondary/40 text-left"
                            data-testid={`from-crew-option-${c.id}`}>
                            <span className="font-medium text-foreground">{c.name}</span>
                            <span className="text-muted-foreground">{c.rank} · {c.base}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* To crew */}
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5 block">
                  Replacement Crew Member {fromCrew && <span className="text-muted-foreground/60">(same rank category as {fromCrew.rank})</span>}
                </label>
                {toCrew ? (
                  <div className="flex items-center justify-between p-2.5 bg-background/60 border border-primary/30 rounded-sm">
                    <div>
                      <div className="text-xs font-semibold text-foreground">{toCrew.name}</div>
                      <div className="text-[11px] text-muted-foreground">{toCrew.rank} · {toCrew.base}</div>
                    </div>
                    <button onClick={() => { setToCrew(null); setToQuery(""); }} className="text-muted-foreground hover:text-red-400"><X size={12} /></button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search size={11} className="absolute left-2.5 top-2.5 text-muted-foreground" />
                    <input value={toQuery} onChange={e => setToQuery(e.target.value)} placeholder="Search replacement crew..."
                      disabled={!fromCrew}
                      className="w-full pl-7 pr-3 py-2 bg-background border border-border rounded-sm text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-40"
                      data-testid="to-crew-search" />
                    {toResults.length > 0 && (
                      <div className="absolute z-20 top-full mt-1 left-0 right-0 bg-card border border-border rounded-sm shadow-xl max-h-44 overflow-y-auto">
                        {toResults.map(c => (
                          <button key={c.id} onClick={() => { setToCrew(c); setToQuery(""); }}
                            className="w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-secondary/40 text-left"
                            data-testid={`to-crew-option-${c.id}`}>
                            <span className="font-medium text-foreground">{c.name}</span>
                            <span className="text-muted-foreground">{c.rank} · {c.base}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Flight + reason */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5 block">Flight Number</label>
                  <input value={flight} onChange={e => setFlight(e.target.value)} placeholder="QP1101"
                    className="w-full px-3 py-2 bg-background border border-border rounded-sm text-xs font-mono text-foreground uppercase focus:outline-none focus:ring-1 focus:ring-primary"
                    data-testid="new-swap-flight" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5 block">Reason</label>
                  <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason for swap..."
                    className="w-full px-3 py-2 bg-background border border-border rounded-sm text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                </div>
              </div>
            </>
          )}

          <div className="flex justify-between pt-2 border-t border-border">
            <button onClick={onClose} className="px-3 py-2 border border-border text-xs text-muted-foreground rounded-sm hover:text-foreground">Cancel</button>
            <button onClick={handleSubmit} disabled={!fromCrew || !toCrew || !flight.trim() || loading}
              className="flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground text-xs font-bold rounded-sm hover:bg-primary/90 disabled:opacity-40"
              data-testid="submit-swap-btn">
              {loading ? <><RefreshCw size={11} className="animate-spin" /> Submitting...</> : <><Plus size={11} /> Submit Request</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Respond Modal ────────────────────────────────────────────────────────────

function RespondModal({ request, onClose, onDone }) {
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  async function handle(accept) {
    setLoading(true);
    try {
      await swapRequestsApi.respond(request.id, { accept, message });
      toast.success(accept ? `${request.to_crew_name} accepted — now awaiting ops approval` : "Response recorded — swap declined");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setLoading(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="respond-modal">
      <div className="bg-card border border-border rounded-sm w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="text-sm font-black text-foreground">Respond to Swap Request</div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="p-3 bg-background/60 border border-border rounded-sm text-xs">
            <div className="text-muted-foreground mb-1">Responding as: <span className="text-foreground font-semibold">{request.to_crew_name}</span></div>
            <div className="text-muted-foreground">Swap flight <span className="font-mono text-foreground">{request.flight_number}</span> with <span className="text-foreground font-semibold">{request.from_crew_name}</span></div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5 block">Note (optional)</label>
            <input value={message} onChange={e => setMessage(e.target.value)} placeholder="Any note to ops..."
              className="w-full px-3 py-2 bg-background border border-border rounded-sm text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
          <div className="flex gap-3 pt-2 border-t border-border">
            <button onClick={() => handle(false)} disabled={loading}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 border border-red-500/40 text-red-400 text-xs font-bold rounded-sm hover:bg-red-500/10 disabled:opacity-40"
              data-testid="respond-decline-btn">
              <X size={11} /> Decline
            </button>
            <button onClick={() => handle(true)} disabled={loading}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-green-600 text-white text-xs font-bold rounded-sm hover:bg-green-700 disabled:opacity-40"
              data-testid="respond-accept-btn">
              {loading ? <RefreshCw size={11} className="animate-spin" /> : <Check size={11} />} Accept
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Review Modal ─────────────────────────────────────────────────────────────

function ReviewModal({ request, onClose, onDone }) {
  const [notes,   setNotes]   = useState("");
  const [loading, setLoading] = useState(false);

  async function handle(approve) {
    setLoading(true);
    try {
      await swapRequestsApi.review(request.id, { approve, ops_notes: notes });
      toast.success(approve ? "Swap approved — assignment updated" : "Swap rejected");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setLoading(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="review-modal">
      <div className="bg-card border border-border rounded-sm w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="text-sm font-black text-foreground">Ops Manager Review</div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="p-3 bg-background/60 border border-border rounded-sm space-y-1.5 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Requesting:</span>
              <span className="font-semibold text-foreground">{request.from_crew_name} ({request.from_crew_rank})</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Replacement:</span>
              <span className="font-semibold text-foreground">{request.to_crew_name} ({request.to_crew_rank})</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Flight:</span>
              <span className="font-mono text-foreground">{request.flight_number}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Reason:</span>
              <span className="text-foreground max-w-[60%] text-right">{request.reason || "—"}</span>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5 block">Ops Notes</label>
            <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Reason for approval / rejection..."
              className="w-full px-3 py-2 bg-background border border-border rounded-sm text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
          <div className="flex gap-3 pt-2 border-t border-border">
            <button onClick={() => handle(false)} disabled={loading}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 border border-red-500/40 text-red-400 text-xs font-bold rounded-sm hover:bg-red-500/10 disabled:opacity-40"
              data-testid="reject-swap-btn">
              <X size={11} /> Reject
            </button>
            <button onClick={() => handle(true)} disabled={loading}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-green-600 text-white text-xs font-bold rounded-sm hover:bg-green-700 disabled:opacity-40"
              data-testid="approve-swap-btn">
              {loading ? <RefreshCw size={11} className="animate-spin" /> : <Check size={11} />} Approve
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Request Row ──────────────────────────────────────────────────────────────

function RequestRow({ req, onRespond, onReview, onCancel }) {
  const meta = STATUS_META[req.status] || {};
  const Icon = meta.icon || AlertCircle;

  return (
    <div className="bg-card border border-border rounded-sm p-3.5 hover:border-primary/25 transition-colors"
      data-testid={`swap-row-${req.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        {/* Crew info */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className={`flex items-center justify-center w-7 h-7 rounded-sm border shrink-0 ${meta.border} ${meta.bg}`}>
            <Icon size={13} className={meta.color} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-bold text-foreground truncate">{req.from_crew_name}</span>
              <ArrowRightLeft size={10} className="text-muted-foreground shrink-0" />
              <span className="text-xs font-bold text-foreground truncate">{req.to_crew_name}</span>
            </div>
            <div className="flex items-center gap-2 mt-0.5 text-[11px] text-muted-foreground flex-wrap">
              <span className="font-mono text-primary">{req.flight_number}</span>
              <span>·</span>
              <span>{req.from_crew_rank}</span>
              <span>·</span>
              <span>{req.from_crew_base} → {req.to_crew_base}</span>
              {req.reason && <><span>·</span><span className="italic truncate max-w-[200px]">"{req.reason}"</span></>}
            </div>
          </div>
        </div>

        {/* Status + actions */}
        <div className="flex items-center gap-2 shrink-0">
          {/* Status badge */}
          <span className={`text-[10px] font-bold px-2 py-0.5 border rounded-sm ${meta.border} ${meta.bg} ${meta.color}`}>
            {meta.label || req.status}
          </span>

          {/* Timeline */}
          <span className="text-[10px] text-muted-foreground">{timeAgo(req.created_at)}</span>

          {/* Action buttons */}
          {req.status === "pending_response" && (
            <>
              <button onClick={() => onRespond(req)}
                className="flex items-center gap-1 px-2.5 py-1 border border-blue-500/40 text-blue-400 text-[10px] font-bold rounded-sm hover:bg-blue-500/10 transition-colors"
                data-testid={`respond-btn-${req.id}`}>
                <User size={9} /> Respond
              </button>
              <button onClick={() => onCancel(req)}
                className="p-1 text-muted-foreground hover:text-red-400 transition-colors"
                data-testid={`cancel-btn-${req.id}`}>
                <X size={12} />
              </button>
            </>
          )}
          {req.status === "pending_approval" && (
            <button onClick={() => onReview(req)}
              className="flex items-center gap-1 px-2.5 py-1 bg-primary text-primary-foreground text-[10px] font-bold rounded-sm hover:bg-primary/90 transition-colors"
              data-testid={`review-btn-${req.id}`}>
              <Check size={9} /> Review
            </button>
          )}
        </div>
      </div>

      {/* Ops notes footer */}
      {req.ops_notes && (
        <div className="mt-2 pt-2 border-t border-border text-[11px] text-muted-foreground">
          <span className="text-muted-foreground/60">Ops note: </span>{req.ops_notes}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function SwapRequests() {
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [activeTab, setActiveTab] = useState("ALL");
  const [showNew,   setShowNew]   = useState(false);
  const [responding,setResponding]= useState(null);   // request obj
  const [reviewing, setReviewing] = useState(null);   // request obj

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await swapRequestsApi.list();
      setData(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Tab filtering
  const visibleRequests = useMemo(() => {
    if (!data) return [];
    const reqs = data.requests || [];
    if (activeTab === "ALL") return reqs;
    if (activeTab === "rejected") return reqs.filter(r => r.status === "rejected" || r.status === "rejected_by_peer");
    return reqs.filter(r => r.status === activeTab);
  }, [data, activeTab]);

  async function handleCancel(req) {
    try {
      await swapRequestsApi.cancel(req.id);
      toast.info("Swap request cancelled");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
  }

  function exportData() {
    exportCSV({
      headers: [
        { label: "From",         key: "from_crew_name"  },
        { label: "From Rank",    key: "from_crew_rank"  },
        { label: "To",           key: "to_crew_name"    },
        { label: "To Rank",      key: "to_crew_rank"    },
        { label: "Flight",       key: "flight_number"   },
        { label: "Status",       key: "status_label"    },
        { label: "Reason",       key: "reason"          },
        { label: "Ops Notes",    key: "ops_notes"       },
        { label: "Created",      key: "created_at"      },
        { label: "Actioned At",  key: "actioned_at"     },
      ],
      rows: visibleRequests,
      filename: `swap-requests-${todayStr()}.csv`,
    });
  }

  const S = data?.summary || {};
  const pending = (S.pending_response || 0) + (S.pending_approval || 0);

  return (
    <div className="p-6 space-y-5" data-testid="swap-requests-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-foreground tracking-tight">Swap Requests</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Crew self-service swap workflow · {S.total || 0} total
            {pending > 0 && <span className="text-amber-400 font-semibold"> · {pending} requiring action</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={exportData} className="flex items-center gap-1.5 px-3 py-2 border border-border text-xs text-muted-foreground hover:text-foreground rounded-sm transition-colors" data-testid="export-swaps-btn">
            Export CSV
          </button>
          <button onClick={load} disabled={loading} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50 transition-colors">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          </button>
          <button onClick={() => setShowNew(true)}
            className="flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground text-xs font-bold rounded-sm hover:bg-primary/90 transition-colors"
            data-testid="new-swap-request-btn">
            <Plus size={12} /> New Request
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-5 gap-3" data-testid="swap-kpi-strip">
        {[
          { label: "Total",            val: S.total             || 0, color: "text-foreground"        },
          { label: "Awaiting Response",val: S.pending_response  || 0, color: "text-amber-400"         },
          { label: "Awaiting Approval",val: S.pending_approval  || 0, color: "text-blue-400"          },
          { label: "Approved",         val: S.approved          || 0, color: "text-green-400"         },
          { label: "Rejected",         val: S.rejected          || 0, color: "text-red-400"           },
        ].map(({ label, val, color }) => (
          <div key={label} className="bg-card border border-border rounded-sm p-3 text-center" data-testid={`swap-kpi-${label.toLowerCase().replace(/\s+/g,"-")}`}>
            <div className={`text-xl font-black ${color}`}>{val}</div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-widest mt-0.5 leading-tight">{label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-border pb-0">
        {TABS.map(t => {
          const cnt = t.key === "ALL" ? (S.total || 0) : (S[t.count_key] || 0);
          return (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border-b-2 -mb-px transition-colors ${activeTab === t.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid={`tab-${t.key}`}>
              {t.label}
              {cnt > 0 && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-bold ${activeTab === t.key ? "bg-primary/20 text-primary" : "bg-secondary text-muted-foreground"}`}>{cnt}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Request list */}
      {loading && !data ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground gap-2">
          <RefreshCw size={16} className="animate-spin" /> Loading requests...
        </div>
      ) : visibleRequests.length === 0 ? (
        <div className="text-center py-16">
          <ArrowRightLeft size={32} className="text-muted-foreground/30 mx-auto mb-3" />
          <div className="text-sm text-muted-foreground">No swap requests in this category</div>
          {activeTab === "ALL" && (
            <button onClick={() => setShowNew(true)} className="mt-3 text-xs text-primary hover:underline" data-testid="empty-new-swap-btn">
              Create the first request
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {visibleRequests.map(req => (
            <RequestRow key={req.id} req={req}
              onRespond={setResponding}
              onReview={setReviewing}
              onCancel={handleCancel}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {showNew && (
        <NewSwapModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />
      )}
      {responding && (
        <RespondModal request={responding} onClose={() => setResponding(null)} onDone={() => { setResponding(null); load(); }} />
      )}
      {reviewing && (
        <ReviewModal request={reviewing} onClose={() => setReviewing(null)} onDone={() => { setReviewing(null); load(); }} />
      )}
    </div>
  );
}
